import React, { useCallback, useContext, useMemo } from 'react'
import LineUserIcon from 'LuxKit/Icons/line/LineUserIcon'
import OfferFilterDropdownBox from '../OfferFilterDropdownBox'
import { OfferPageActions } from 'contexts/OfferPage/offerPageStateReducer'
import withOfferPageState from 'contexts/OfferPage/withOfferPageState'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import { countOccupants, formatOccupantsForSelectionBox } from 'lib/offer/occupancyUtils'
import OfferPageStateTrackingContext from 'contexts/OfferPage/offerPageStateTrackingContext'
import { isTourV2Offer } from 'lib/offer/offerTypes'
import { EmptyArray } from 'lib/array/arrayUtils'
import noop from 'lib/function/noop'
import { isSalesforceTourV2 } from 'lib/offer/offerUtils'
import { hasSelectedRooms } from 'lib/offer/offerPageUtil'
import { useIntl } from 'react-intl'

interface Props {
  rooms?: Array<App.Occupants>
  open: boolean
  offer: App.Offer | App.BedbankOffer | App.Tours.TourV2Offer
  inSidebar?: boolean
  occupantUnit?: 'guest' | 'traveller'
  onClick?: (e?: React.MouseEvent<HTMLButtonElement>) => void
  multiRoomDisabled?: boolean
  iconDisabled?: boolean
  className?: string
  ref?: React.Ref<HTMLButtonElement>
}

function OfferCapacityWithAgesSelectBox(props: Props) {
  const {
    open,
    rooms = EmptyArray,
    offer,
    inSidebar,
    occupantUnit = 'guest',
    onClick = noop,
    multiRoomDisabled,
    iconDisabled,
    className,
    ref,
  } = props
  const intl = useIntl()
  const dispatch = useOfferPageDispatch()
  const allRooms = useMemo(() => countOccupants(rooms), [rooms])
  const { trackEvent } = useContext(OfferPageStateTrackingContext)
  const hasRooms = hasSelectedRooms(rooms)

  const onBoxClick = useCallback<React.MouseEventHandler<HTMLButtonElement>>((e) => {
    if (open) {
      dispatch({ type: OfferPageActions.closeFilter })
      trackEvent('OCCUPANCY_FILTERS_CLOSED')
    } else {
      dispatch({ type: OfferPageActions.toggleFilter, filter: 'capacity' })
      trackEvent('OCCUPANCY_FILTERS_PRESSED')
      onClick(e)
    }
  }, [open, onClick, dispatch, trackEvent])

  const showInvalid = useMemo(() => !open && allRooms.childrenAge?.some(age => age === -1), [allRooms, open])
  const roomText = useMemo(() => {
    if (multiRoomDisabled) return undefined
    const count = rooms.length
    const saleUnit = offer.saleUnit
    switch (saleUnit) {
      case 'room':
        return intl.formatMessage({
          id: 'zGcJ0nDZlRsySAgRTRmU',
          defaultMessage: '({count, plural, one {# room} other {# rooms}})',
          description: 'Parenthesised room count in capacity selection box',
        }, { count })
      case 'suite':
        return intl.formatMessage({
          id: 'j_T5ozHxipIwwm-v1rhd',
          defaultMessage: '({count, plural, one {# suite} other {# suites}})',
          description: 'Parenthesised suite count in capacity selection box',
        }, { count })
      case 'villa':
        return intl.formatMessage({
          id: 'mSeG4h5WywT71EEeJ0sg',
          defaultMessage: '({count, plural, one {# villa} other {# villas}})',
          description: 'Parenthesised villa count in capacity selection box',
        }, { count })
      case 'cabin':
        return intl.formatMessage({
          id: 'UDCdnJfWlxyFIM5Ay5_F',
          defaultMessage: '({count, plural, one {# cabin} other {# cabins}})',
          description: 'Parenthesised cabin count in capacity selection box',
        }, { count })
      default:
        return intl.formatMessage({
          id: '1C5rAgfLy9Z1i19wzir3',
          defaultMessage: '({count} {saleUnit})',
          description: 'Parenthesised generic sale unit count in capacity selection box',
        }, { count, saleUnit })
    }
  }, [intl, multiRoomDisabled, offer.saleUnit, rooms.length])

  const value = useMemo(() => {
    if (hasRooms) {
      const occupantsText = formatOccupantsForSelectionBox(isTourV2Offer(offer) && isSalesforceTourV2(offer), allRooms)
      return <><b>{occupantsText}</b>{roomText ? ` ${roomText}` : ''}</>
    }
  }, [allRooms, offer, hasRooms, roomText])

  const placeholderText = occupantUnit === 'traveller'
    ? intl.formatMessage({
        id: 'u8OaAgS716N6UudwdETu',
        defaultMessage: 'Select travellers',
        description: 'Placeholder text for traveller selection dropdown on the form',
      })
    : intl.formatMessage({
        id: 'ZOF9Ob0i8n0UCeKC2PiA',
        defaultMessage: 'Select guests',
        description: 'Placeholder text for guest selection dropdown on the offer detail page',
      })

  return (
    <OfferFilterDropdownBox
      className={className}
      onClick={onBoxClick}
      active={open}
      ref={ref}
      invalid={showInvalid}
      invalidMessage={intl.formatMessage({
        id: 'se2qQS-6XjuRYwasuPW5',
        defaultMessage: 'Please select children’s ages',
        description: 'Validation error message when children ages are not selected',
      })}
      data-testid="OfferCapacitySelectBox"
      icon={!iconDisabled && <LineUserIcon />}
      inSidebar={inSidebar}
      label={intl.formatMessage({
        id: '6o41-ceqxsIGrDNaN_eD',
        defaultMessage: 'Guests',
        description: 'Label for occupancy selection dropdown in the occupancy or guest picker',
      })}
      placeholder={placeholderText}
      value={value}
    />
  )
}

export default withOfferPageState(state => ({
  rooms: state.rooms,
  open: state.activeFilter === 'capacity',
  offer: state.offer ?? state.bedbankOffer ?? state.tourV2Offer,
}), OfferCapacityWithAgesSelectBox)
